import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { eventsAPI } from '../../utils/api';
import { format } from 'date-fns';
import PublicLayout from '../../components/layout/PublicLayout';
import { CalendarIcon, MapPinIcon, ClockIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import useSEO from '../../hooks/useSEO';

export default function EventDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [event, setEvent]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useSEO({
    title: event ? `${event.title} — JNF` : 'Event — JNF',
    description: event?.description?.replace(/<[^>]+>/g, '').slice(0, 160) || '',
  });

  useEffect(() => {
    setLoading(true);
    eventsAPI.getOne(slug)
      .then(r => {
        if (r.data?.data) setEvent(r.data.data);
        else setNotFound(true);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <PublicLayout>
      <div className="max-w-4xl mx-auto px-4 py-16">
        <div className="h-72 bg-gray-100 rounded-2xl animate-pulse mb-6" />
        <div className="space-y-3">
          <div className="h-8 bg-gray-100 rounded-xl animate-pulse w-3/4" />
          <div className="h-4 bg-gray-100 rounded animate-pulse w-1/2" />
          <div className="h-4 bg-gray-100 rounded animate-pulse" />
          <div className="h-4 bg-gray-100 rounded animate-pulse w-5/6" />
        </div>
      </div>
    </PublicLayout>
  );

  if (notFound) return (
    <PublicLayout>
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        <div className="text-6xl mb-6">🌙</div>
        <h1 className="text-2xl font-bold text-navy-800 mb-3">Event Not Found</h1>
        <p className="text-gray-400 mb-8">This event may have been removed or the link is incorrect.</p>
        <Link to="/events" className="btn-primary inline-flex items-center gap-2">
          <ArrowLeftIcon className="h-4 w-4" /> Back to Events
        </Link>
      </div>
    </PublicLayout>
  );

  const eventDate   = event.event_date ? new Date(event.event_date) : null;
  const endDate     = event.end_date   ? new Date(event.end_date)   : null;
  const isPast      = eventDate && eventDate < new Date();
  const isCancelled = event.status === 'cancelled';

  return (
    <PublicLayout>
      <div className="max-w-4xl mx-auto px-4 py-10">

        {/* Back link */}
        <Link to="/events" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-navy-600 transition-colors mb-6">
          <ArrowLeftIcon className="h-4 w-4" /> All Events
        </Link>

        {/* Cover */}
        {event.cover_url ? (
          <div className="w-full h-64 md:h-96 rounded-3xl overflow-hidden mb-8 shadow-sm">
            <img src={event.cover_url} alt={event.title} className="w-full h-full object-cover" />
          </div>
        ) : (
          <div className="w-full h-48 md:h-64 rounded-3xl overflow-hidden mb-8 bg-gradient-to-br from-navy-700 to-navy-900 flex items-center justify-center">
            <span className="text-6xl">🌙</span>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">

          {/* Main content */}
          <div className="lg:col-span-2">

            {/* Status badges */}
            <div className="flex flex-wrap gap-2 mb-4">
              {event.is_featured && (
                <span className="badge bg-cyan-100 text-cyan-700 text-xs">⭐ Featured</span>
              )}
              {isCancelled && (
                <span className="badge bg-red-100 text-red-700 text-xs">❌ Cancelled</span>
              )}
              {isPast && !isCancelled && (
                <span className="badge bg-gray-100 text-gray-500 text-xs">Past Event</span>
              )}
            </div>

            <h1 className="text-2xl md:text-3xl font-bold text-navy-800 mb-2 leading-tight">
              {event.title}
            </h1>
            {event.title_ar && (
              <p className="arabic text-navy-600 text-xl mb-4">{event.title_ar}</p>
            )}

            {/* Description */}
            {event.description && (
              <div className="mt-6">
                <div
                  className="prose max-w-none text-gray-700 leading-relaxed text-sm md:text-base"
                  dangerouslySetInnerHTML={{ __html: event.description }}
                />
              </div>
            )}
            {event.description_ar && (
              <div className="mt-4 p-4 bg-navy-50 rounded-2xl border border-navy-100">
                <p className="arabic text-navy-700 leading-loose">{event.description_ar}</p>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4">

            {/* Date & time card */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
              <h3 className="font-semibold text-navy-700 text-sm uppercase tracking-wide">Event Details</h3>

              {eventDate && (
                <div className="flex items-start gap-3">
                  <CalendarIcon className="h-5 w-5 text-cyan-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-navy-800 font-semibold text-sm">
                      {format(eventDate, 'EEEE, MMMM d, yyyy')}
                    </p>
                    <p className="text-gray-400 text-xs mt-0.5">
                      {format(eventDate, 'h:mm a')}
                      {endDate && ` — ${format(endDate, 'h:mm a')}`}
                    </p>
                  </div>
                </div>
              )}

              {event.location && (
                <div className="flex items-start gap-3">
                  <MapPinIcon className="h-5 w-5 text-cyan-500 mt-0.5 flex-shrink-0" />
                  <p className="text-navy-800 text-sm">{event.location}</p>
                </div>
              )}

              {endDate && endDate.toDateString() !== eventDate?.toDateString() && (
                <div className="flex items-start gap-3">
                  <ClockIcon className="h-5 w-5 text-cyan-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-gray-500 text-xs">Ends</p>
                    <p className="text-navy-800 text-sm font-medium">{format(endDate, 'EEEE, MMMM d, yyyy')}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Share / back */}
            <Link
              to="/events"
              className="w-full btn-outline flex items-center justify-center gap-2 text-sm py-3"
            >
              <ArrowLeftIcon className="h-4 w-4" /> View All Events
            </Link>
          </div>

        </div>
      </div>
    </PublicLayout>
  );
}
